import React, { useState, useEffect } from 'react';
import { updateReportStatus } from '../../firebase';
import { syncWorkHoursForReport } from '../../services/workHoursService';

const EnhancedConclusionModal = ({ 
  selectedReport, 
  onClose, 
  onReportUpdate,
  showNotesForm,
  updatingId 
}) => {
  const [adminNotes, setAdminNotes] = useState('');
  const [standardHours, setStandardHours] = useState('');
  const [overtimeHours, setOvertimeHours] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form quando si apre un nuovo report
  useEffect(() => {
    if (selectedReport) {
      setAdminNotes('');
      setStandardHours('');
      setOvertimeHours('');
      setErrors({});
    }
  }, [selectedReport]);

  // Validazione form
  const validateForm = () => {
    const newErrors = {};
    
    // Validazione ore standard (numeri interi 0-8 o lettere speciali)
    if (standardHours) {
      const value = standardHours.toString().toUpperCase();
      const isNumber = !isNaN(value) && value !== '';
      const isSpecialLetter = ['A', 'F', 'P', 'C', 'M'].includes(value);
      
      if (isNumber) {
        const numValue = parseFloat(value);
        // Controlla che sia un numero intero
        if (!Number.isInteger(numValue)) {
          newErrors.standardHours = 'Sono ammessi solo numeri interi (0, 1, 2, ..., 8)';
        } else if (numValue < 0 || numValue > 8) {
          newErrors.standardHours = 'Inserisci un valore tra 0 e 8';
        }
      } else if (!isSpecialLetter) {
        newErrors.standardHours = 'Inserisci un numero intero (0-8) o una lettera speciale (A, F, P, C, M)';
      }
      
      // Se è una lettera speciale, non permettere straordinari
      if (isSpecialLetter && overtimeHours) {
        newErrors.overtimeHours = 'Non è possibile inserire straordinari con lettere speciali';
      }
    }
    
    // Validazione ore straordinario (solo numeri interi)
    if (overtimeHours) {
      const numValue = parseFloat(overtimeHours);
      if (isNaN(numValue)) {
        newErrors.overtimeHours = 'Inserisci un numero valido';
      } else if (!Number.isInteger(numValue)) {
        newErrors.overtimeHours = 'Sono ammessi solo numeri interi (0, 1, 2, ..., 12)';
      } else if (numValue < 0 || numValue > 12) {
        newErrors.overtimeHours = 'Inserisci un valore tra 0 e 12';
      }
      
      // Controlla che le ore standard siano numeriche se ci sono straordinari
      if (standardHours) {
        const stdValue = standardHours.toString().toUpperCase();
        const isSpecialLetter = ['A', 'F', 'P', 'C', 'M'].includes(stdValue);
        if (isSpecialLetter) {
          newErrors.overtimeHours = 'Non è possibile inserire straordinari con lettere speciali';
        }
      } else {
        newErrors.standardHours = 'Le ore standard sono obbligatorie se si inseriscono straordinari';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formatta la data per il display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Gestisce la conclusione della segnalazione
  const handleConcludeReport = async () => {
    if (!validateForm()) return;
    
    setIsProcessing(true);
    
    try {
      // 1. Aggiorna lo stato della segnalazione a "Conclusa"
      console.log('Aggiornamento stato segnalazione...');
      await updateReportStatus(selectedReport.id, 'Conclusa', adminNotes);
      
      // 2. Se sono state inserite le ore, sincronizza con workHours
      if (standardHours || overtimeHours) {
        console.log('Sincronizzazione ore con workHours...');
        
        const hoursData = {
          date: selectedReport.date,
          standardHours: standardHours,
          overtimeHours: parseFloat(overtimeHours) || 0,
          notes: adminNotes || `Correzione da segnalazione del ${formatDate(selectedReport.date)}`,
          userId: selectedReport.userId
        };
        
        // Simula la funzione di sincronizzazione (da implementare in workHoursService)
        await syncWorkHoursForReport(hoursData);
        console.log('Ore sincronizzate con successo');
      }
      
      // 3. Aggiorna la UI locale
      onReportUpdate(selectedReport.id, 'Conclusa', adminNotes);
      
      // 4. Chiudi il modal
      onClose();
      
      alert('Segnalazione conclusa con successo!' + 
            (standardHours || overtimeHours ? ' Le ore sono state sincronizzate nel calendario.' : ''));
      
    } catch (error) {
      console.error('Errore nella conclusione della segnalazione:', error);
      alert('Errore nella conclusione della segnalazione. Riprova.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Gestisce la cancellazione
  const handleCancel = () => {
    setAdminNotes('');
    setStandardHours('');
    setOvertimeHours('');
    setErrors({});
    onClose();
  };

  if (!showNotesForm || !selectedReport) return null;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '0px',
        width: '90%',
        maxWidth: '700px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        maxHeight: '90vh',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 25px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <h4 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '600' }}>
            📋 Conclusione Segnalazione
          </h4>
          <button 
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: 'white',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseOver={(e) => e.target.style.opacity = '1'}
            onMouseOut={(e) => e.target.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
        
        {/* Body */}
        <div className="modal-body" style={{ 
          padding: '25px',
          maxHeight: 'calc(90vh - 140px)',
          overflowY: 'auto'
        }}>
          {/* Info Segnalazione */}
          <div className="report-info" style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '25px',
            border: '1px solid #e9ecef'
          }}>
            <h5 style={{ 
              margin: '0 0 15px 0', 
              color: '#495057',
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              📄 Dettagli Segnalazione
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <p style={{ margin: 0 }}>
                <strong>Data:</strong> {formatDate(selectedReport.date)}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Utente:</strong> {selectedReport.userName || selectedReport.userEmail}
              </p>
            </div>
            <p style={{ margin: '12px 0 0 0' }}>
              <strong>Descrizione:</strong> {selectedReport.description}
            </p>
          </div>

          {/* Sezione Correzione Ore */}
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '25px'
          }}>
            <h5 style={{ 
              margin: '0 0 15px 0', 
              color: '#856404',
              fontSize: '1.1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center'
            }}>
              ⏰ Correzione Ore Lavorative (Opzionale)
            </h5>
            <p style={{ 
              margin: '0 0 20px 0', 
              color: '#856404',
              fontSize: '0.95rem'
            }}>
              Se la segnalazione riguarda correzioni alle ore, inserisci i valori corretti che verranno 
              automaticamente sincronizzati nel calendario ore dell'utente.<br/>
              <strong>Ore Standard:</strong> numero intero 0-8 o lettere speciali (A=Assenza, F=Ferie, P=Permesso, C=Cassa Int., M=Malattia)<br/>
              <strong>Straordinari:</strong> solo numeri interi 0-12 (disponibili solo con ore standard numeriche)
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '20px',
              marginBottom: '15px'
            }}>
              {/* Ore Standard */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>
                  🕐 Ore Standard:
                </label>
                <input
                  type="text"
                  value={standardHours}
                  onChange={(e) => {
                    let value = e.target.value.toUpperCase();
                    
                    // Se è un numero, controlla che sia intero
                    if (!isNaN(value) && value !== '') {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 8) {
                        value = numValue.toString();
                      } else if (value.includes('.')) {
                        // Rimuovi decimali
                        value = value.split('.')[0];
                      }
                    }
                    
                    setStandardHours(value);
                    if (errors.standardHours) {
                      setErrors(prev => ({ ...prev, standardHours: null }));
                    }
                    // Se è una lettera speciale, pulisci gli straordinari
                    if (['A', 'F', 'P', 'C', 'M'].includes(value)) {
                      setOvertimeHours('');
                      if (errors.overtimeHours) {
                        setErrors(prev => ({ ...prev, overtimeHours: null }));
                      }
                    }
                  }}
                  placeholder="0-8 o A/F/P/C/M"
                  maxLength="2"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: errors.standardHours ? '2px solid #dc3545' : '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s',
                    textTransform: 'uppercase'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#80bdff'}
                  onBlur={(e) => e.target.style.borderColor = errors.standardHours ? '#dc3545' : '#ced4da'}
                />
                {errors.standardHours && (
                  <small style={{ color: '#dc3545', fontSize: '0.85rem' }}>
                    {errors.standardHours}
                  </small>
                )}
                <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                  Numero intero 0-8 o lettere: A(Assenza), F(Ferie), P(Permesso), C(Cassa Int.), M(Malattia)
                </small>
              </div>

              {/* Ore Straordinario */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>
                  ⚡ Ore Straordinario:
                </label>
                <input
                  type="number"
                  value={overtimeHours}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Accetta solo numeri interi
                    if (value === '' || (parseInt(value, 10).toString() === value && parseInt(value, 10) >= 0 && parseInt(value, 10) <= 12)) {
                      setOvertimeHours(value);
                      if (errors.overtimeHours) {
                        setErrors(prev => ({ ...prev, overtimeHours: null }));
                      }
                    }
                  }}
                  min="0"
                  max="12"
                  step="1"
                  placeholder="0-12"
                  disabled={standardHours && ['A', 'F', 'P', 'C', 'M'].includes(standardHours.toString().toUpperCase())}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: errors.overtimeHours ? '2px solid #dc3545' : '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s',
                    backgroundColor: (standardHours && ['A', 'F', 'P', 'C', 'M'].includes(standardHours.toString().toUpperCase())) ? '#f8f9fa' : 'white',
                    cursor: (standardHours && ['A', 'F', 'P', 'C', 'M'].includes(standardHours.toString().toUpperCase())) ? 'not-allowed' : 'text'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#80bdff'}
                  onBlur={(e) => e.target.style.borderColor = errors.overtimeHours ? '#dc3545' : '#ced4da'}
                />
                {errors.overtimeHours && (
                  <small style={{ color: '#dc3545', fontSize: '0.85rem' }}>
                    {errors.overtimeHours}
                  </small>
                )}
                <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                  {(standardHours && ['A', 'F', 'P', 'C', 'M'].includes(standardHours.toString().toUpperCase())) 
                    ? 'Non disponibile con lettere speciali' 
                    : 'Solo numeri interi da 0 a 12'}
                </small>
              </div>
            </div>

            {/* Info sulla sincronizzazione */}
            {(standardHours || overtimeHours) && (
              <div style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '6px',
                padding: '12px',
                fontSize: '0.9rem',
                color: '#0c5460'
              }}>
                <strong>🔄 Sincronizzazione:</strong> Le ore inserite sostituiranno 
                automaticamente quelle esistenti per la data {formatDate(selectedReport.date)} 
                nel calendario ore dell'utente.
              </div>
            )}
          </div>

          {/* Note Admin */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '10px',
              fontWeight: '600',
              color: '#495057',
              fontSize: '1rem'
            }}>
              📝 Note per l'utente (opzionale):
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Inserisci le note per informare l'utente sulle operazioni effettuate (es. 'Ore corrette: modificate correttamente')"
              rows="4"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '0.95rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#80bdff'}
              onBlur={(e) => e.target.style.borderColor = '#ced4da'}
            />
            <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
              Queste note saranno visibili all'utente e lo informeranno delle correzioni effettuate.
            </small>
          </div>
        </div>
        
        {/* Footer */}
        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '20px 25px',
          borderTop: '1px solid #e9ecef',
          backgroundColor: '#f8f9fa'
        }}>
          <button 
            onClick={handleCancel}
            disabled={isProcessing}
            style={{
              padding: '12px 24px',
              border: '1px solid #6c757d',
              backgroundColor: 'white',
              color: '#6c757d',
              borderRadius: '6px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              opacity: isProcessing ? 0.6 : 1
            }}
            onMouseOver={(e) => !isProcessing && (e.target.style.backgroundColor = '#6c757d', e.target.style.color = 'white')}
            onMouseOut={(e) => !isProcessing && (e.target.style.backgroundColor = 'white', e.target.style.color = '#6c757d')}
          >
            Annulla
          </button>
          <button 
            onClick={handleConcludeReport}
            disabled={isProcessing || updatingId === selectedReport?.id}
            style={{
              padding: '12px 24px',
              border: '1px solid #28a745',
              backgroundColor: '#28a745',
              color: 'white',
              borderRadius: '6px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              opacity: isProcessing ? 0.6 : 1
            }}
            onMouseOver={(e) => !isProcessing && (e.target.style.backgroundColor = '#218838')}
            onMouseOut={(e) => !isProcessing && (e.target.style.backgroundColor = '#28a745')}
          >
            {isProcessing ? (
              <>
                <span style={{ marginRight: '8px' }}>⏳</span>
                Elaborazione...
              </>
            ) : (
              <>
                <span style={{ marginRight: '8px' }}>✅</span>
                Conclude Segnalazione
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedConclusionModal;